import { useState, useRef, useCallback, useEffect } from 'react';
import { getSupportedOpusMimeType } from '@/lib/audioCapabilityDetector';
import type { AudioSource, StreamStatus } from './useMixedAudioRecorder';

// Re-export types for convenience
export type { AudioSource, StreamStatus };

/**
 * Opus録音フックの戻り値
 * UseWavRecorderReturnと互換性を保つ
 */
export interface UseOpusRecorderReturn {
  /** 録音を開始する */
  startRecording: () => Promise<boolean>;
  /** 録音を停止する */
  stopRecording: () => void;
  /** 録音された音声チャンク（WebM+Opus形式Blob）の配列 */
  audioChunks: Blob[];
  /** エラー情報 */
  error: Error | null;
  /** 録音中かどうか */
  isRecording: boolean;
  /** マイクのステータス */
  micStatus: StreamStatus;
  /** タブ音声のステータス */
  tabStatus: StreamStatus;
  /** タブ音声のラベル */
  tabLabel: string | null;
}

/**
 * オプション設定
 */
export interface UseOpusRecorderOptions {
  /** 音声ソース */
  audioSource: AudioSource;
  /** 事前選択済みタブ音声ストリーム */
  tabStream?: MediaStream | null;
}

const CHUNK_INTERVAL_MS = 2500;

/**
 * MediaRecorderを使用してWebM+Opus形式で録音するカスタムフック
 *
 * useWavRecorderの代替として、API Gateway WebSocketの32KB制限に対応。
 * チャンク間隔ごとにMediaRecorderをstop/startすることで、各チャンクが完全なWebMヘッダーを持つ
 * 独立したファイルとして生成される（サーバー側のFFmpeg処理に対応）。
 *
 * 技術仕様:
 * - コーデック: Opus (WebMコンテナ)
 * - ビットレート: 48kbps (ASRに十分な情報量)
 * - チャンク間隔: 3000ms (stop/startサイクル)
 * - 予想チャンクサイズ: ~12 KB (Base64後)
 * - フォールバック: Chrome/Firefox/Edge → WebM+Opus, Safari → MP4+Opus or エラー
 * - 各チャンクは完全なWebMファイル（EBMLヘッダー + Segment + Cluster）
 *
 * @param options - 録音オプション
 * @returns 録音機能を提供するオブジェクト
 *
 * @example
 * ```tsx
 * const { startRecording, stopRecording, audioChunks, micStatus, tabStatus } = useOpusRecorder({
 *   audioSource: 'both',
 * });
 *
 * // 録音開始
 * await startRecording();
 *
 * // 録音停止（自動的にWebM+Opus形式でaudioChunksに追加される）
 * stopRecording();
 * ```
 */
export function useOpusRecorder(options: UseOpusRecorderOptions): UseOpusRecorderReturn {
  const { audioSource, tabStream: selectedTabStream = null } = options;

  const [audioChunks, setAudioChunks] = useState<Blob[]>([]);
  const [error, setError] = useState<Error | null>(null);
  const [isRecording, setIsRecording] = useState(false);
  const [micStatus, setMicStatus] = useState<StreamStatus>('inactive');
  const [tabStatus, setTabStatus] = useState<StreamStatus>('inactive');
  const [tabLabel, setTabLabel] = useState<string | null>(null);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const micStreamRef = useRef<MediaStream | null>(null);
  const tabStreamRef = useRef<MediaStream | null>(null);
  const tabStreamOwnedRef = useRef(false);
  const audioContextRef = useRef<AudioContext | null>(null);
  const isStartingRef = useRef(false); // レースコンディション防止用フラグ
  const recordingIntervalRef = useRef<NodeJS.Timeout | null>(null); // チャンク間隔ごとのstop用タイマー
  const isRecordingRef = useRef(false); // stopイベント内で参照するための録音状態

  useEffect(() => {
    if (selectedTabStream) {
      const tabLabelFromTrack =
        selectedTabStream.getVideoTracks()[0]?.label || selectedTabStream.getAudioTracks()[0]?.label || null;
      setTabLabel(tabLabelFromTrack && tabLabelFromTrack.trim() ? tabLabelFromTrack : null);
      return;
    }
    if (audioSource !== 'tab' && audioSource !== 'both') {
      setTabLabel(null);
    }
  }, [selectedTabStream, audioSource]);

  /**
   * 録音を開始
   */
  const startRecording = useCallback(async () => {
    // レースコンディション対策: 録音中または開始処理中の二重呼び出しを防ぐ
    if (isRecording || isStartingRef.current) {
      console.warn('[useOpusRecorder] Already recording or starting');
      return false;
    }

    // 開始処理中フラグを立てる
    isStartingRef.current = true;

    // ブラウザAPIチェック
    if (typeof window === 'undefined') {
      console.error('[useOpusRecorder] SSR環境');
      setError(new Error('Window is not defined (SSR environment)'));
      isStartingRef.current = false;
      return false;
    }

    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      console.error('[useOpusRecorder] getUserMedia非対応');
      setError(new Error('getUserMedia is not supported in this browser'));
      isStartingRef.current = false;
      return false;
    }

    // MIMEタイプ取得とサポートチェック
    const selectedMimeType = getSupportedOpusMimeType();
    if (!selectedMimeType) {
      const errorMsg =
        'Opus recording is not supported in this browser. ' +
        'Please use Chrome, Firefox, or Edge for Opus recording. ' +
        'Safari users may need to use WAV format.';
      console.error('[useOpusRecorder]', errorMsg);
      setError(new Error(errorMsg));
      isStartingRef.current = false;
      return false;
    }

    console.log('[useOpusRecorder] Selected MIME type:', selectedMimeType);

    try {
      if (audioSource === 'mic') {
        setTabLabel(null);
      }

      // 音声チャンクをリセット
      setAudioChunks([]);
      setError(null);

      let finalStream: MediaStream;

      // 音声ソースに応じて処理を分岐
      if (audioSource === 'mic') {
        // マイクのみ
        const micStream = await navigator.mediaDevices.getUserMedia({ audio: true });
        micStreamRef.current = micStream;
        setMicStatus('active');
        finalStream = micStream;
        console.log('[useOpusRecorder] Mic stream acquired');
      } else if (audioSource === 'tab') {
        // タブのみ
        const tabStream = selectedTabStream;
        if (!tabStream) {
          throw new Error('システム音声が未選択です。入力先を選択してください。');
        }
        const tabLabelFromTrack =
          tabStream.getVideoTracks()[0]?.label || tabStream.getAudioTracks()[0]?.label || null;
        setTabLabel(tabLabelFromTrack && tabLabelFromTrack.trim() ? tabLabelFromTrack : null);
        tabStreamRef.current = tabStream;
        tabStreamOwnedRef.current = false;
        setTabStatus('active');
        finalStream = tabStream;
        console.log('[useOpusRecorder] Tab stream acquired');
      } else {
        // both: マイク + タブ
        let micStream: MediaStream | null = null;
        let tabStream: MediaStream | null = null;

        // マイク取得
        try {
          micStream = await navigator.mediaDevices.getUserMedia({ audio: true });
          micStreamRef.current = micStream;
          setMicStatus('active');
          console.log('[useOpusRecorder] Mic stream acquired (both mode)');
        } catch (micError) {
          console.warn('[useOpusRecorder] マイク権限が拒否されました:', micError);
          setMicStatus('denied');
        }

        // タブ取得
        tabStream = selectedTabStream;
        if (tabStream) {
          const tabLabelFromTrack =
            tabStream.getVideoTracks()[0]?.label || tabStream.getAudioTracks()[0]?.label || null;
          setTabLabel(tabLabelFromTrack && tabLabelFromTrack.trim() ? tabLabelFromTrack : null);
          tabStreamRef.current = tabStream;
          tabStreamOwnedRef.current = false;
          setTabStatus('active');
          console.log('[useOpusRecorder] Tab stream acquired (both mode)');
        } else {
          setTabStatus('denied');
          setTabLabel(null);
        }

        // 両方拒否された場合はエラー
        if (!micStream && !tabStream) {
          throw new Error('マイクとシステム音声の両方の権限が拒否されました');
        }

        // どちらか一方のみ取得できた場合
        if (!micStream && tabStream) {
          finalStream = tabStream;
        } else if (micStream && !tabStream) {
          finalStream = micStream;
        } else if (micStream && tabStream) {
          // 両方取得できた場合: Web Audio APIでミックス
          const AudioContextClass =
            window.AudioContext ||
            ((window as typeof window & { webkitAudioContext?: typeof AudioContext })
              .webkitAudioContext as typeof AudioContext);
          const audioContext = new AudioContextClass();
          audioContextRef.current = audioContext;

          // MediaStreamSourceを作成
          const micSource = audioContext.createMediaStreamSource(micStream);
          const tabSource = audioContext.createMediaStreamSource(tabStream);

          // GainNodeを作成（音量調整用、デフォルト1.0）
          const micGain = audioContext.createGain();
          const tabGain = audioContext.createGain();
          micGain.gain.value = 1.0;
          tabGain.gain.value = 1.0;

          // DynamicsCompressorNodeでクリッピング防止
          const compressor = audioContext.createDynamicsCompressor();
          compressor.threshold.value = -24;
          compressor.knee.value = 30;
          compressor.ratio.value = 12;
          compressor.attack.value = 0.003;
          compressor.release.value = 0.25;

          // Destinationを作成
          const destination = audioContext.createMediaStreamDestination();

          // 音声グラフを接続: mic/tab → gain → compressor → destination
          micSource.connect(micGain).connect(compressor);
          tabSource.connect(tabGain).connect(compressor);
          compressor.connect(destination);

          finalStream = destination.stream;
          console.log('[useOpusRecorder] Streams mixed via Web Audio API');
        } else {
          // TypeScriptの型チェックのための分岐（到達不可能）
          throw new Error('Unreachable code');
        }
      }

      // MediaRecorder初期化
      const mediaRecorder = new MediaRecorder(finalStream, {
        mimeType: selectedMimeType,
        audioBitsPerSecond: 48000, // 48kbps (ASRに十分な情報量)
      });
      mediaRecorderRef.current = mediaRecorder;

      console.log('[useOpusRecorder] MediaRecorder created:', {
        mimeType: selectedMimeType,
        audioBitsPerSecond: 48000,
        timeslice: CHUNK_INTERVAL_MS,
      });

      // dataavailableイベント: チャンクが利用可能になった時
      mediaRecorder.addEventListener('dataavailable', (event) => {
        if (event.data && event.data.size > 0) {
          const sizeKB = (event.data.size / 1024).toFixed(2);
          const withinLimit = event.data.size <= 32000 ? '✅' : '❌';

          console.log('[useOpusRecorder] Chunk generated:', {
            size: event.data.size,
            type: event.data.type,
            sizeKB: `${sizeKB} KB`,
            withinLimit,
            timestamp: new Date().toISOString(),
          });

          // チャンクサイズ警告
          if (event.data.size > 32000) {
            console.warn(
              `[useOpusRecorder] WARNING: Chunk size (${sizeKB} KB) exceeds API Gateway limit (32 KB)`
            );
          }

          setAudioChunks((prev) => [...prev, event.data]);
        }
      });

      // errorイベント: エラー発生時
      mediaRecorder.addEventListener('error', (event) => {
        const errorMessage =
          event instanceof ErrorEvent ? event.message : 'Unknown recording error';
        console.error('[useOpusRecorder] MediaRecorderエラー:', errorMessage);
        setError(new Error(`MediaRecorder error: ${errorMessage}`));
        setIsRecording(false);
        isRecordingRef.current = false;
      });

      // stopイベント: 録音停止時に次のチャンクを自動的に開始
      mediaRecorder.addEventListener('stop', () => {
        // 録音中なら次の録音を開始（stopRecording()で明示的に停止されていない場合のみ）
        if (isRecordingRef.current && mediaRecorderRef.current) {
          setTimeout(() => {
            if (
              isRecordingRef.current &&
              mediaRecorderRef.current &&
              mediaRecorderRef.current.state === 'inactive'
            ) {
              try {
                mediaRecorderRef.current.start();
                console.log('[useOpusRecorder] Next recording chunk started');
              } catch (err) {
                console.error('[useOpusRecorder] Failed to restart recording:', err);
              }
            }
          }, 10); // 10ms待機してから次の録音を開始（チャンク境界の欠落を最小化）
        }
      });

      // 録音開始（完全なWebMファイルを生成するためtimesliceなし）
      mediaRecorder.start();
      setIsRecording(true);
      isRecordingRef.current = true;

      // チャンク間隔ごとにMediaRecorderを停止するタイマーを設定
      recordingIntervalRef.current = setInterval(() => {
        if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
          console.log('[useOpusRecorder] Stopping chunk to create complete WebM file');
          mediaRecorderRef.current.stop();
        }
      }, CHUNK_INTERVAL_MS);

      console.log('[useOpusRecorder] Recording started (stop/start pattern for complete WebM chunks)');
      return true;
    } catch (err) {
      const error =
        err instanceof DOMException || err instanceof Error ? err : new Error(String(err));
      console.error('[useOpusRecorder] Failed to start recording:', error);
      setError(error);
      setIsRecording(false);

      // ストリームをクリーンアップ
      if (micStreamRef.current) {
        micStreamRef.current.getTracks().forEach((track) => track.stop());
        micStreamRef.current = null;
        setMicStatus('inactive');
      }
      if (tabStreamRef.current && tabStreamOwnedRef.current) {
        tabStreamRef.current.getTracks().forEach((track) => track.stop());
      }
      tabStreamRef.current = null;
      tabStreamOwnedRef.current = false;
      setTabStatus('inactive');
      if (audioContextRef.current) {
        audioContextRef.current.close().catch((err) => {
          console.warn('[useOpusRecorder] Failed to close AudioContext:', err);
        });
        audioContextRef.current = null;
      }
      return false;
    } finally {
      isStartingRef.current = false;
    }
  }, [isRecording, audioSource, selectedTabStream]);

  /**
   * 録音を停止
   */
  const stopRecording = useCallback(() => {
    if (!isRecording) {
      console.warn('[useOpusRecorder] Not recording');
      return;
    }

    console.log('[useOpusRecorder] Stopping recording');

    // 録音状態をfalseに設定（stopイベントでの自動再開を防ぐ）
    isRecordingRef.current = false;
    setIsRecording(false);

    // タイマーをクリア
    if (recordingIntervalRef.current) {
      clearInterval(recordingIntervalRef.current);
      recordingIntervalRef.current = null;
    }

    // MediaRecorderを停止
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
      mediaRecorderRef.current = null;
    }

    // マイクストリームをクリーンアップ
    if (micStreamRef.current) {
      micStreamRef.current.getTracks().forEach((track) => track.stop());
      micStreamRef.current = null;
      setMicStatus('inactive');
      console.log('[useOpusRecorder] Mic stream stopped');
    }

    // タブストリームをクリーンアップ
    if (tabStreamRef.current && tabStreamOwnedRef.current) {
      tabStreamRef.current.getTracks().forEach((track) => track.stop());
    }
    tabStreamRef.current = null;
    tabStreamOwnedRef.current = false;
    setTabStatus('inactive');
    console.log('[useOpusRecorder] Tab stream stopped');

    // AudioContextをクリーンアップ
    if (audioContextRef.current) {
      audioContextRef.current.close().catch((err) => {
        console.warn('[useOpusRecorder] Failed to close AudioContext:', err);
      });
      audioContextRef.current = null;
      console.log('[useOpusRecorder] AudioContext closed');
    }

    console.log('[useOpusRecorder] Recording stopped, total chunks:', audioChunks.length);
  }, [isRecording, audioChunks.length]);

  /**
   * アンマウント時のクリーンアップ
   */
  useEffect(() => {
    return () => {
      // タイマーをクリア
      if (recordingIntervalRef.current) {
        clearInterval(recordingIntervalRef.current);
        recordingIntervalRef.current = null;
      }

      // 録音状態をfalseに設定
      isRecordingRef.current = false;

      // MediaRecorderを停止
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.stop();
        mediaRecorderRef.current = null;
      }

      // すべてのストリームをクリーンアップ
      if (micStreamRef.current) {
        micStreamRef.current.getTracks().forEach((track) => track.stop());
        micStreamRef.current = null;
      }
      if (tabStreamRef.current && tabStreamOwnedRef.current) {
        tabStreamRef.current.getTracks().forEach((track) => track.stop());
      }
      tabStreamRef.current = null;
      tabStreamOwnedRef.current = false;

      // AudioContextをクリーンアップ
      if (audioContextRef.current) {
        audioContextRef.current.close().catch((err) => {
          console.warn('[useOpusRecorder] Failed to close AudioContext during cleanup:', err);
        });
        audioContextRef.current = null;
      }

      console.log('[useOpusRecorder] Cleanup on unmount');
    };
  }, []);

  return {
    startRecording,
    stopRecording,
    audioChunks,
    error,
    isRecording,
    micStatus,
    tabStatus,
    tabLabel,
  };
}
